/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import axios from 'axios';
import _ from 'lodash';
import { toHeaderCase ,toSentenceCase,toUpperCase} from 'js-convert-case';
import humanizeString from 'humanize-string';
import moment from 'moment';

import config from '../config/credentialDetail.config.json';

import { useCredId } from '../provider';

import { GraphqlServerModule } from '@vlinder-web/graphql-server-module-react';
import { GET_CREDENTIAL_DETAIL } from '../graphql';
import { VApplication } from '../../../application';
import { AppNavigatorService } from '../../appNavigatorService';

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync('services.GraphqlServerModule') as GraphqlServerModule;
};

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync('services.AppNavigatorService') as AppNavigatorService;
};

export interface ICredentialDetailMethods extends IPluginMethodMap {
  getCredentialDetailProps(args: any): Promise<any>;
}

function isValidHttpUrl(string: string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
}

export class CredentialDetailService implements IAgentPlugin {
  readonly methods: ICredentialDetailMethods;

  constructor() {
    this.methods = {
      getCredentialDetailProps: this.getCredentialDetailProps.bind(this),
    };
  }

  private formatGetListItem1Title(credential: any) {
    let res = credential?.signers?.map((_signer) => {
      return _signer?.title;
    });
    return res;
  }

  async getCredentialDetailProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient('graphql-gateway');
    const { credId } = useCredId();
    try {
      if (client) {
        const res = await client.query({
          query: GET_CREDENTIAL_DETAIL,
          variables: {
            verifyCredentialId: credId,
          },
          fetchPolicy: 'network-only',
        });
        if (res?.data) {
          return {
            data: res?.data?.verifyCredential,
            isWalletRegistered: this.getWalletRegistered(
              res?.data?.verifyCredential
            ),
            // imageItems: this.formatImages(res?.data?.verifyCredential?.image),
            logoSrc: res?.data?.verifyCredential?.issuerLogo,
            pdfFile: res?.data?.verifyCredential?.pdf,
            title: this.formatTitle(res?.data?.verifyCredential),
            badgeTitle:this.formatBadgeTitle(res?.data?.verifyCredential),
         
            badgeTitleBg: this.formatBadgeTitleBg(res?.data?.verifyCredential),
            subtitle: this.formatSubTitle(res?.data?.verifyCredential),
            description: res?.data?.verifyCredential?.description,
            credential: this.formatAttributes(res?.data?.verifyCredential),
            buttons: await this.formatProductActions(
              res?.data?.verifyCredential
            ),
            attributeItems: this.formatAttributeItems(
              res?.data?.verifyCredential
            ),
            tableItems: this.formatDigitalSignatures(
              res?.data?.verifyCredential
            ),
            carouselItems: this.formatCarouselItems(
              res?.data?.verifyCredential
            ),
            listItemTitle: this.formatGetListItem1Title(
              res?.data?.verifyCredential
            ),
          };
        }
      }
    } catch (err: any) {
      return err;
    }
  }

  private async formatProductActions(credential?: any) {
    const serv = getWhoamiSrv();
    let user = await serv?.whoAmI();
    const formatted: any = [];
    if (
      credential?.approveStatus === 'UNDER_REVIEW' &&
      (user?.whoami === 'admin' || user?.whoami === 'master admin')
    ) {
      formatted.push(config?.revokeCertificate);
      if (credential?.holderStatus !== 'Removed') {
        formatted.push(config?.resendCredential);
      }
    }
    return formatted;
  }

  private getWalletRegistered(credential: any) {
    let isWalletRegistered = false;

    if (credential?.accountStatus === 'WALLET_REGISTERED') {
      isWalletRegistered = true;
    }

    return isWalletRegistered;
  }

  private formatAttributes(credential?: any) {
    let _attributeData: any = [];
    let formatted: any = { ...(credential?.credentialSubject || {}) };
    if (formatted) {
      for (let key in formatted) {
        if (config?.credentialSubject?.blacklistedKeys?.includes(key)) {
          delete formatted[key];
        }
      }
    }

    Object.entries(formatted).forEach(([key, value]) => {
      if (!Array.isArray(value)) {
        _attributeData.push({
          label: toHeaderCase(key ?? ''),
          value: value,
        });
      }
    });
    return _attributeData;
  }

  private formatBadgeTitleBg(credential?: any) {
    let formatted: any = 'danger';
    if (credential?.status?.toLowerCase()?.includes('active')) {
      formatted = 'success';
    }
    return formatted;
  }

  private formatTitle(credential: any) {
    let formatted = credential?.credentialName ?? '';
    return toHeaderCase(formatted);
  }
  private formatBadgeTitle(credential: any) {
    const approveStatus = humanizeString(credential?.approveStatus);
    const status = humanizeString(credential?.status);
  
    let formatted = approveStatus ?? status;
  
    if (formatted) {
      return toUpperCase(formatted);
    } else {
      return '';
    }
  
  }

  private formatSubTitle(credential: any) {
    let formatted =
    credential?.signers ? credential?.signers[credential?.signers?.length - 1]?.name ?? '' : '';
    return toHeaderCase(formatted);
  }

  private formatImages(image?: string) {
    const formatted: any = [];
    if (image) {
      formatted.push({
        original: `data:image/png;base64,${image}`,
        thumbnail: `data:image/png;base64,${image}`,
      });
    }
    return formatted;
  }

  private formatGetListItemTitle() {
    return config?.digitalSignatures?.title;
  }

  private formatAttributeItems(credential: any) {
    let _credential = {};
    if (credential.signers) {
      _credential = credential.signers;
    }
    let ans: any = [];
    if (_credential) {
      // for (let item = 0; item < _credential.length; item++) {
      //   let temp = config?.attributeItems[0];
      //   for (let credKey in _credential[item]) {
      //     console.log("sds",_credential[item])
      //     if (credKey === "name") {
      //       temp = { ...temp, value: _credential[item][credKey]  };
      //     }
      //   }
      //   ans.push(temp);
      // }
      for (
        let item = _credential.length - 1;
        item < _credential.length;
        item++
      ) {
        let temp = config?.attributeItems[0];
        for (let credKey in _credential[item]) {
          console.log('sds', _credential[item]);
          if (credKey === 'name') {
            temp = { ...temp, value: _credential[item][credKey] };
          }
        }
        ans.push(temp);
      }
      console.log(ans, 'ans->');
      let temp = config?.attributeItems;
      temp = temp
        .filter((item) => item.id !== 'digitalSignedBy')
        .map((_item) => {
          if (_item.id === 'validUntil') {
            return {
              ..._item,
              value: this.formatDate(credential[_item.id] ?? null),
            };
          }

          if (_item.id === 'validFrom') {
            return { ..._item,value: this.formatFromDate(credential[_item.id] ?? null), };

          }
          if (_item.id === 'status') {
            return { ..._item, value: credential.approveStatus };
          }
          return { ..._item, value: credential[_item.id] };
        });
      return [...ans, ...temp];
    }

    console.log('ans', ans);
    return ans;
  }

  private formatDigitalSignatures(credential: any) {
    let _credential = {};
    if (credential.signers) {
      _credential = credential.signers;
    }
    let ans: any = [];
    if (_credential) {
      for (let item = 0; item < _credential.length; item++) {
        let temp = config.digitalSignatures1.info;
        for (let credKey in _credential[item]) {
          if (credKey === 'createdAt') {
            temp = temp.map((_item) => {
              if (_item.id === 'createdAt') {
                return {
                  ..._item,
                  value: this.formatFromDate(
                    _credential[item][credKey] ?? null
                  ),
                };
              }
              return _item;
            });
          } else if (credKey === 'did') {
            temp = temp.map((_item) => {
              if (_item.id === 'did') {
                return { ..._item, value: _credential[item][credKey] };
              }
              return _item;
            });
          } else if (credKey === 'pk') {
            temp = temp.map((_item) => {
              if (_item.id === 'pk') {
                return { ..._item, value: _credential[item][credKey] };
              }
              return _item;
            });
          } else if (credKey === 'name') {
            temp = temp.map((_item) => {
              if (_item.id === 'name') {
                return { ..._item, value: _credential[item][credKey] };
              }
              return _item;
            });
          } else if (credKey === 'address') {
            temp = temp.map((_item) => {
              if (_item.id === 'address') {
                return { ..._item, value: _credential[item][credKey] };
              }
              return _item;
            });
          } else if (credKey === 'isValid') {
            temp = temp.map((_item) => {
              if (_item.id === 'isValid') {
                return {
                  ..._item,
                  value: _credential[item][credKey] ? 'VALID' : 'INVALID',
                };
              }
              return _item;
            });
          } else {
          }
        }
        ans = [...ans, [...temp]];
      }
    }
    return ans;
  }

  private formatDate(date: string) {
    if (date) {
      return moment(date).format('lll');
    } else {
      return 'Perpetual';
    }
  }

  private formatFromDate(date: string) {
    if (date) {
      return moment(date).format('lll');
    } else {
      return '-';
    }
  }

  private formatCarouselItems(credential: any) {
    let _docs: any = [];
    if (credential?.pdf) {
      const src = isValidHttpUrl(credential?.pdf)
        ? credential?.pdf
        : `data:application/pdf;base64,${credential?.pdf}`;
      _docs.push({
        type: 'pdf',
        poster: credential?.logo,
        src: src,
        title: credential?.credentialName,
      });
    }
    Object.entries(credential?.credentialSubject).forEach(([key, value]) => {
      if (Array.isArray(value) && value?.length > 0) {
        value.forEach((key) => {
          if (key?.type && key?.file) {
            let _type = key?.type?.split('/');
            _docs.push({
              type:
                _type[0] === 'image'
                  ? 'img'
                  : _type[0] === 'video'
                  ? 'video'
                  : _type[0] === 'audio'
                  ? 'audio'
                  : 'pdf',
              poster: credential?.logo,
              src: key?.base64,
              title: credential?.credentialName,
            });
          }
        });
      }
    });

    Array.isArray(credential?.evidence) &&
      credential?.evidence.map((item: any) => {
        let _type = item?.evidenceDocumentType?.split('/');
        _docs.push({
          type:
            _type[0] === 'image'
              ? 'img'
              : _type[0] === 'video'
              ? 'video'
              : _type[0] === 'audio'
              ? 'audio'
              : 'pdf',
          poster: item?.poster,
          src: item?.id,
          title: item?.evidenceDocument?.replace(/\.[^/.]+$/, ''),
        });
      });
    return _docs;
  }
}
